import React, { useEffect, useState } from "react";
import "../pages/AdminProjects.css";
import bfLogo from "../assets/bf_logo.png";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, FileText, Download, ExternalLink } from "lucide-react";

/* ===== PDF BIBLIOTEKOS ===== */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ===== FIREBASE ===== */
import { db, auth } from "../firebase";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const latinize = (str) => { 
  if (!str) return ""; 
  return str
    .replace(/[ąĄ]/g, 'a').replace(/[čČ]/g, 'c').replace(/[ęĘ]/g, 'e')
    .replace(/[ėĖ]/g, 'e').replace(/[įĮ]/g, 'i').replace(/[šŠ]/g, 's')
    .replace(/[ųŲ]/g, 'u').replace(/[ūŪ]/g, 'u').replace(/[žŽ]/g, 'z')
    .replace(/[üÜ]/g, 'ue').replace(/[äÄ]/g, 'ae').replace(/[öÖ]/g, 'oe')
    .replace(/[ß]/g, 'ss');
};

export default function AdminCompanyDocumentView() {
  const navigate = useNavigate();
  const { docId } = useParams();
  const { t } = useTranslation();

  const [docData, setDocData] = useState(null);
  const [workersMap, setWorkersMap] = useState({}); 
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);

  const getFileType = (url) => {
    if (!url) return 'unknown';
    const cleanUrl = url.split(/[?#]/)[0].toLowerCase();
    if (cleanUrl.endsWith('.pdf')) return 'pdf';
    if (cleanUrl.match(/\.(jpg|jpeg|png|gif|webp)$/)) return 'image';
    return 'other';
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) {
          const uData = userSnap.data();
          const masterId = uData.belongsTo || user.uid;
          const masterSnap = await getDoc(doc(db, "users", masterId));
          if (masterSnap.exists()) setUserData(masterSnap.data());
        }
      } else {
        navigate("/admin/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!docId || !currentUser) return;
    const loadData = async () => {
      try {
        const mySnap = await getDoc(doc(db, "users", currentUser.uid));
        const masterId = mySnap.exists() ? (mySnap.data().belongsTo || currentUser.uid) : currentUser.uid;

        const docSnap = await getDoc(doc(db, "company_docs", docId));
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.adminId === masterId) setDocData(data);
        }

        const qWorkers = query(collection(db, "workers"), where("adminId", "==", masterId));
        const workersSnap = await getDocs(qWorkers);
        const map = {};
        workersSnap.forEach(w => { map[w.id] = w.data().name; });
        setWorkersMap(map);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    loadData();
  }, [docId, currentUser]);

  const handleDownloadPDF = () => {
    if (!userData || !docData) return;
    const pdf = new jsPDF('p', 'mm', 'a4');
    pdf.setFontSize(22);
    pdf.text(latinize(userData?.companyName || "BuildForce"), 14, 20);
    // Čia galima pridėti autoTable su parašais
    pdf.save(`Protokoll_${latinize(docData.title)}.pdf`);
  };

  if (loading) return <div className="doc-view-container">{t('loading')}...</div>;
  if (!docData) return <div className="doc-view-container" style={{color: 'red'}}>{t('error_not_found')}</div>;

  const fileType = getFileType(docData.fileUrl);

  return (
    <div className="doc-view-container">
      <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: '1100px', margin: '0 auto 20px'}}>
          <button className="back-btn" onClick={() => navigate(-1)}>← {t('back')}</button>
          <button className="bf-green-pdf-btn" onClick={handleDownloadPDF} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Download size={18} /> <span>PDF PROTOKOLL</span>
          </button>
      </div>

      <img src={bfLogo} alt="BF" className="doc-view-logo" />
      <h1 className="doc-view-title">{docData.title}</h1>

      <div className="doc-view-box">
        <div style={{display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start'}}>
          
          {/* FAILO PERŽIŪRA */}
          <div style={{flex: 2, minWidth: '300px'}}>
            {docData.fileUrl ? (
              <>
                <a href={docData.fileUrl} target="_blank" rel="noreferrer" className="gold-glow" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '12px', marginBottom: '15px', textDecoration: 'none', fontWeight: 'bold', color: '#d4af37', background: '#0a0d12', borderRadius: '8px'}}>
                    <ExternalLink size={20} /> {t('open_file')}
                </a>
                <div style={{borderRadius: '12px', overflow: 'hidden', background: '#000'}}>
                  {fileType === 'pdf' ? (
                    <iframe src={docData.fileUrl} title="Preview" className="doc-frame" />
                  ) : (
                    <img src={docData.fileUrl} alt="Preview" className="doc-image" />
                  )}
                </div>
              </>
            ) : (
              <div style={{padding: '50px', textAlign: 'center', color: '#666'}}>
                <FileText size={48} />
                <p>{t('no_file_preview')}</p>
              </div>
            )}
          </div>

          {/* PARAŠŲ SĄRAŠAS */}
          {docData.requireSignature && (
            <div className="signatures-sidebar">
              <h3 style={{marginTop: 0, fontSize: '18px', borderBottom: '1px solid #334155', paddingBottom: '12px', color: '#00ff88'}}>
                {t('signatures')} ({docData.signatures?.length || 0})
              </h3>
              <div className="signatures-hidden-scroll">
                {docData.signatures?.length === 0 && <div style={{color: '#888', fontStyle: 'italic', padding: '20px'}}>{t('no_signatures')}</div>}
                {docData.signatures?.map((workerId, index) => (
                  <div key={index} className="sig-item">
                    <CheckCircle size={16} color="#00ff88" />
                    <span>{workersMap[workerId] || "..."}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}